import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Instagram, Youtube } from "lucide-react";

function MaskedLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ settings }) {
  const ref = useRef(null);
  const [imgError, setImgError] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const avatarY = useTransform(scrollYProgress, [0, 1], [0, -30]);

  return (
    <header ref={ref} className="relative" data-testid="storefront-hero">
      <motion.div
        style={{ y: glowY }}
        aria-hidden
        className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-black/[0.05] blur-3xl"
      />
      <motion.div style={{ y: avatarY }} className="relative">
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="h-20 w-20 overflow-hidden rounded-3xl border border-black/10 bg-white shadow-[0_12px_32px_-12px_rgba(0,0,0,0.2)]"
        >
          {imgError || !settings.profile_image ? (
            <div className="flex h-full w-full items-center justify-center bg-black font-heading text-xl font-bold text-white">
              Ti
            </div>
          ) : (
            <img
              src={settings.profile_image}
              alt="TECH iELA"
              onError={() => setImgError(true)}
              className="h-full w-full object-cover"
              data-testid="profile-image"
            />
          )}
        </motion.div>
      </motion.div>

      <h1 className="mt-8 whitespace-nowrap font-heading text-4xl font-light leading-[0.95] tracking-tighter sm:text-6xl" data-testid="hero-title">
        <MaskedLine delay={0.15}>
          TECH <span className="font-semibold">iELA</span>
        </MaskedLine>
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mt-5 max-w-sm text-base leading-relaxed text-zinc-500"
        data-testid="hero-bio"
      >
        {settings.bio || "Tech I recommend, all in one place."}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mt-7 flex items-center gap-3"
        data-testid="social-links"
      >
        {settings.instagram_url && (
          <a
            href={settings.instagram_url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            data-testid="hero-instagram-link"
            className="flex h-11 w-11 items-center justify-center rounded-2xl border border-black/10 bg-white text-zinc-600 transition-colors duration-200 hover:border-black/25 hover:bg-zinc-50 hover:text-black active:scale-[0.96]"
          >
            <Instagram className="h-[18px] w-[18px]" strokeWidth={1.75} />
          </a>
        )}
        {settings.youtube_url && (
          <a
            href={settings.youtube_url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="YouTube"
            data-testid="hero-youtube-link"
            className="flex h-11 w-11 items-center justify-center rounded-2xl border border-black/10 bg-white text-zinc-600 transition-colors duration-200 hover:border-black/25 hover:bg-zinc-50 hover:text-black active:scale-[0.96]"
          >
            <Youtube className="h-[18px] w-[18px]" strokeWidth={1.75} />
          </a>
        )}
        {settings.twitter_url && (
          <a
            href={settings.twitter_url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="X"
            data-testid="hero-twitter-link"
            className="flex h-11 w-11 items-center justify-center rounded-2xl border border-black/10 bg-white text-zinc-600 transition-colors duration-200 hover:border-black/25 hover:bg-zinc-50 hover:text-black active:scale-[0.96]"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden>
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
        )}
      </motion.div>
    </header>
  );
}
